import { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Avatar, 
  Grid, CircularProgress, Alert, Snackbar, Card, CardContent
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const ProfileSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required')
});

export default function Profile() {
  const { currentUser } = useAuth();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  if (!currentUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>Profile</Typography>
      
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar
              sx={{ width: 120, height: 120, mb: 2 }}
              src="/broken-image.jpg"
            />
            <Typography variant="h6">{currentUser.email}</Typography>
            <Typography variant="body2" color="text.secondary">
              Role: {currentUser.role}
            </Typography>
            
            <Card sx={{ mt: 3, width: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Account Details</Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography>{currentUser.email}</Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Role</Typography>
                  <Typography>{currentUser.role}</Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">User ID</Typography>
                  <Typography>{currentUser.id}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Formik
              initialValues={{
                email: currentUser.email || '',
                firstName: currentUser.firstName || '',
                lastName: currentUser.lastName || ''
              }}
              validationSchema={ProfileSchema}
              onSubmit={(values, { setSubmitting }) => {
                // Simulate API call
                setTimeout(() => {
                  setSnackbar({ 
                    open: true, 
                    message: 'Profile updated successfully', 
                    severity: 'success' 
                  });
                  setSubmitting(false);
                }, 1000);
              }}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                  <Field
                    as={TextField}
                    name="email"
                    label="Email"
                    fullWidth
                    margin="normal"
                    disabled
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                  <Field
                    as={TextField}
                    name="firstName"
                    label="First Name"
                    fullWidth
                    margin="normal"
                    error={touched.firstName && !!errors.firstName}
                    helperText={touched.firstName && errors.firstName}
                  />
                  <Field
                    as={TextField}
                    name="lastName"
                    label="Last Name"
                    fullWidth
                    margin="normal"
                    error={touched.lastName && !!errors.lastName}
                    helperText={touched.lastName && errors.lastName}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3 }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <CircularProgress size={24} /> : 'Update Profile'}
                  </Button>
                </Form>
              )}
            </Formik>
            
            <Box sx={{ mt: 5 }}>
              <Typography variant="h6" gutterBottom>Security</Typography>
              <Button
                variant="outlined"
                color="primary"
              >
                Change Password
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
